import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { authApi, LoginPayload } from '../api/auth.api'
import { useAuthStore } from '../../../stores/auth.store'
import { queryClient } from '../../../lib/queryClient'

// ─── Hook principal auth ──────────────────────────────────────────────────────
export function useAuth() {
  return useAuthStore()
}

// ─── Login ────────────────────────────────────────────────────────────────────
export function useLogin() {
  const { setTokensAndUser } = useAuthStore()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (payload: LoginPayload) => authApi.login(payload),
    onSuccess: (data) => {
      if ('requires2fa' in data) {
        navigate('/2fa', { state: { userId: data.userId } })
        return
      }
      // Le backend retourne maintenant user avec roles + permissions
      setTokensAndUser(data.accessToken, data.refreshToken, data.user as any)
      navigate('/dashboard')
    },
  })
}

// ─── Logout ───────────────────────────────────────────────────────────────────
export function useLogout() {
  const { logout } = useAuthStore()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      // Logout même si l'API échoue (token expiré)
      logout()
      navigate('/login')
    },
  })
}

// ─── Permission helpers ───────────────────────────────────────────────────────
export function usePermission(permission: string): boolean {
  return useAuthStore((s) => s.hasPermission(permission))
}

export function useHasRole(role: string): boolean {
  return useAuthStore((s) => s.hasRole(role))
}

// ─── Setup 2FA ────────────────────────────────────────────────────────────────
export function useSetup2FA() {
  return useMutation({ mutationFn: () => authApi.setup2fa() })
}

export function useConfirm2FA() {
  return useMutation({
    mutationFn: (totpCode: string) => authApi.confirm2fa(totpCode),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  })
}

// ─── Mot de passe ────────────────────────────────────────────────────────────
export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => authApi.forgotPassword(email),
  })
}

export function useResetPassword() {
  const navigate = useNavigate()
  return useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      authApi.resetPassword(token, password),
    onSuccess: () => navigate('/login', { state: { passwordReset: true } }),
  })
}

export function useAcceptInvite() {
  const navigate = useNavigate()
  return useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      authApi.acceptInvite(token, password),
    onSuccess: () => navigate('/login', { state: { accountActivated: true } }),
  })
}
